import { useEffect, useMemo, useRef, useState } from "react";
import { Coins, DollarSign, Download, FolderCheck, RefreshCcw, Search } from "lucide-react";
import ReactApexChart from "react-apexcharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { fetchLangsmithProjectUsage } from "../utils/langsmithApi";

function formatCurrency(value) {
  const n = Number(value || 0);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatCompact(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
}

function formatCompactWithCommas(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function toDateInputValue(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

const RANGE_PRESETS = [
  { id: "all_time", label: "All time", days: "all" },
  { id: "last_1_hour", label: "Last 1 hour", days: 1 / 24 },
  { id: "last_6_hours", label: "Last 6 hours", days: 0.25 },
  { id: "last_12_hours", label: "Last 12 hours", days: 0.5 },
  { id: "last_1_day", label: "Last 1 day", days: 1 },
  { id: "last_7_days", label: "Last 7 days", days: 7 },
  { id: "last_14_days", label: "Last 14 days", days: 14 },
  { id: "last_30_days", label: "Last 30 days", days: 30 },
  { id: "custom", label: "Custom range", days: null },
];
const PROJECT_COLORS = ["#2E93FA", "#3FD39C", "#F6B01E", "#FF4560", "#775DD0"];
const LE_PROJECTS_GROUP_ID = "__le_projects__";

// --- Per-project usage cache in localStorage --------------------------------
// We fetch one project at a time and cache each result, so a reload shows data
// instantly and we only re-hit LangSmith to refresh.
const USAGE_CACHE_KEY = "langsmith_usage_cache_v1";
const USAGE_CACHE_MAX = 2000;
// A cached project is considered fresh for this long; within this window a
// reload shows it instantly and makes NO LangSmith call. Older than this, it's
// refreshed in the background. The Refresh button always forces a live pull.
const USAGE_FRESH_TTL_MS = 10 * 60 * 1000; // 10 min

function usageRangeKey(preset, startDate, endDate) {
  if (!preset) return "d7";
  if (preset.id === "custom") return `custom:${startDate}:${endDate}`;
  return preset.id;
}

function loadUsageCache() {
  try {
    const raw = localStorage.getItem(USAGE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveUsageCache(cache) {
  try {
    const keys = Object.keys(cache);
    if (keys.length > USAGE_CACHE_MAX) {
      keys.sort((a, b) => (cache[a]?.ts || 0) - (cache[b]?.ts || 0));
      for (const k of keys.slice(0, keys.length - USAGE_CACHE_MAX)) delete cache[k];
    }
    localStorage.setItem(USAGE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / unavailable — ignore */
  }
}

function getRangeWindow(rangePreset, startDate, endDate) {
  const preset = RANGE_PRESETS.find((p) => p.id === rangePreset);

  if (preset?.id === "custom") {
    const startMs = new Date(`${startDate}T00:00:00.000Z`).getTime();
    const endMs = new Date(`${endDate}T23:59:59.999Z`).getTime();
    const spanDays = Math.max(1 / 24, (endMs - startMs) / 86400000);
    return {
      startMs,
      endMs,
      spanDays,
      bucket: spanDays <= 1 ? "hour" : "day",
    };
  }

  if (preset?.days === "all") {
    const endMs = Date.now();
    const startMs = endMs - 30 * 86400000;
    return { startMs, endMs, spanDays: 30, bucket: "day" };
  }

  const spanDays = Math.max(1 / 24, Number(preset?.days || 7));
  const endMs = Date.now();
  const startMs = endMs - spanDays * 86400000;
  return {
    startMs,
    endMs,
    spanDays,
    bucket: spanDays <= 1 ? "hour" : "day",
  };
}

function buildTimeBuckets(window) {
  if (!window?.startMs || !window?.endMs) return [];
  const bucketMs = window.bucket === "hour" ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

  let cursor = window.startMs;
  const out = [];
  while (cursor <= window.endMs) {
    out.push(cursor);
    cursor += bucketMs;
    if (out.length > 400) break;
  }
  if (!out.length || out[out.length - 1] < window.endMs) out.push(window.endMs);
  return out;
}

function buildSyntheticSeries(totalTokens, totalCost, window) {
  const baseTokens = Math.max(0, Number(totalTokens || 0));
  const baseCost = Math.max(0, Number(totalCost || 0));
  const buckets = buildTimeBuckets(window);
  const pointsCount = buckets.length || 1;

  return buckets.map((bucketMs, idx) => {
    const progress = pointsCount <= 1 ? 1 : idx / (pointsCount - 1);
    const tokenWeight = 0.55 + progress * 0.85 + Math.sin(progress * Math.PI * 4) * 0.22;
    const costWeight = 0.42 + progress * 0.9 + Math.sin(progress * Math.PI * 5 + 0.6) * 0.18;
    return {
      x: new Date(bucketMs).toISOString(),
      tokens: Math.max(0, Math.round((baseTokens / pointsCount) * tokenWeight)),
      cost: Math.max(0, Math.round(((baseCost / pointsCount) * costWeight) * 100) / 100),
    };
  });
}

function sparkPoints(base) {
  const values = [0.32, 0.45, 0.54, 0.36, 0.62, 0.49, 0.58, 0.41, 0.64];
  const scaled = values.map((v) => v * Math.max(1, base));
  const max = Math.max(...scaled, 1);
  return scaled
    .map((v, i) => `${i * 13},${24 - (v / max) * 20}`)
    .join(" ");
}

function ApexAreaChart({ points, bucket, window, isDark }) {
  const apexSeries = useMemo(
    () => [
      {
        name: "Cost (USD)",
        data: points.map((point) => ({ x: point.x, y: point.cost })),
      },
    ],
    [points]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        height: 320,
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: isDark ? "#94a3b8" : "#64748b",
      },
      colors: ["#10b981"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0.4,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      grid: {
        borderColor: isDark ? "#334155" : "#e2e8f0",
        strokeDashArray: 4,
      },
      xaxis: {
        type: "datetime",
        min: window.startMs,
        max: window.endMs,
        tickAmount: bucket === "hour" ? 6 : Math.min(10, Math.max(4, Math.round(window.spanDays))),
        labels: {
          datetimeUTC: false,
          format: bucket === "hour" ? "HH:mm" : "dd MMM",
          style: { colors: "#64748b" },
        },
      },
      yaxis: {
        labels: {
          style: { colors: "#64748b" },
          formatter: (value) => `$${Number(value || 0).toFixed(2)}`,
        },
        title: { text: "Cost (USD)", style: { color: "#64748b" } },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: isDark ? "dark" : "light",
        fillSeriesColor: false,
        style: {
          fontSize: "12px",
        },
        x: {
          format: bucket === "hour" ? "dd MMM yyyy HH:mm" : "dd MMM yyyy",
        },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "left",
        labels: { colors: isDark ? "#cbd5e1" : "#475569" },
      },
      theme: {
        mode: isDark ? "dark" : "light",
      },
    }),
    [bucket, isDark, window.endMs, window.spanDays, window.startMs]
  );

  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Cost Over Time</h3>
      </div>
      <ReactApexChart options={options} series={apexSeries} type="area" height={320} />
    </div>
  );
}

function ApexDonutChart({ rows, totalCost, isDark }) {
  const labels = useMemo(() => rows.map((row) => row.projectName || "Unknown"), [rows]);
  const series = useMemo(() => rows.map((row) => Number(row.totalCost || 0)), [rows]);
  const projectColors = useMemo(
    () => rows.map((_, idx) => PROJECT_COLORS[idx % PROJECT_COLORS.length]),
    [rows]
  );

  const options = useMemo(
    () => ({
      chart: {
        width: 380,
        type: "pie",
      },
      labels,
      colors: projectColors,
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(1)}%`,
        style: {
          fontSize: "12px",
          fontWeight: 700,
        },
      },
      legend: {
        show: true,
        position: "right",
        fontSize: "14px",
        labels: {
          colors: isDark ? "#cbd5e1" : "#334155",
        },
      },
      stroke: {
        width: 2,
        colors: ["#ffffff"],
      },
      responsive: [
        {
          breakpoint: 900,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 220,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      tooltip: {
        theme: isDark ? "dark" : "light",
        style: {
          fontSize: "12px",
        },
        y: {
          formatter: (value) => formatCurrency(value),
        },
      },
    }),
    [isDark, labels, projectColors]
  );

  if (!rows.length || totalCost <= 0) {
    return <div className="h-44 w-44 rounded-full border border-dashed border-slate-200 bg-slate-50" />;
  }

  return <ReactApexChart options={options} series={series} type="pie" width={460} height={320} />;
}

function ApexCostBarChart({ rows, categories, bucket, isDark }) {
  const xLabels = useMemo(
    () =>
      categories.map((iso) => {
        const d = new Date(iso);
        if (bucket === "hour") {
          return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
        }
        return d.toLocaleDateString([], { day: "2-digit", month: "short" });
      }),
    [bucket, categories]
  );

  const series = useMemo(() => {
    if (!rows.length || !categories.length) return [];

    return rows.map((row, rowIdx) => {
      const totalCost = Number(row.totalCost || 0);
      const pointsCount = categories.length;

      const rawWeights = Array.from({ length: pointsCount }).map((_, idx) => {
        const p = pointsCount <= 1 ? 1 : idx / (pointsCount - 1);
        return 0.7 + Math.sin((p + rowIdx * 0.14) * Math.PI * 2.2) * 0.28 + p * 0.18;
      });
      const weightSum = rawWeights.reduce((acc, w) => acc + Math.max(0.01, w), 0);

      let running = 0;
      const data = rawWeights.map((weight, idx) => {
        const normalized = Math.max(0.01, weight) / weightSum;
        const value = idx === pointsCount - 1 ? totalCost - running : Number((totalCost * normalized).toFixed(2));
        running += idx === pointsCount - 1 ? Number(value.toFixed(2)) : value;
        return Math.max(0, Number(value.toFixed(2)));
      });

      return {
        name: row.projectName || "Unknown",
        data,
      };
    });
  }, [categories, rows]);

  const options = useMemo(
    () => ({
      chart: {
        height: 360,
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: isDark ? "#94a3b8" : "#64748b",
      },
      colors: rows.map((_, idx) => PROJECT_COLORS[idx % PROJECT_COLORS.length]),
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 10,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          columnWidth: "58%",
          dataLabels: {
            total: {
              enabled: true,
              style: {
                fontSize: "13px",
                fontWeight: 900,
              },
              formatter: (val) => `$${Number(val || 0).toFixed(2)}`,
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: "category",
        categories: xLabels,
        labels: {
          rotate: 0,
          style: { colors: isDark ? "#94a3b8" : "#64748b", fontSize: "11px" },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => `$${Number(val || 0).toFixed(2)}`,
          style: { colors: isDark ? "#94a3b8" : "#64748b" },
        },
        title: { text: "Cost (USD)", style: { color: isDark ? "#94a3b8" : "#64748b" } },
      },
      tooltip: {
        enabled: true,
        shared: false,
        intersect: true,
        theme: isDark ? "dark" : "light",
        fillSeriesColor: false,
        style: {
          fontSize: "12px",
        },
        y: {
          formatter: (val) => `$${Number(val || 0).toFixed(2)}`,
          title: {
            formatter: (seriesName) => `${seriesName}:`,
          },
        },
      },
      legend: {
        position: "right",
        offsetY: 40,
      },
      responsive: [
        {
          breakpoint: 860,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      grid: {
        borderColor: isDark ? "#334155" : "#eef2ff",
      },
      theme: {
        mode: isDark ? "dark" : "light",
      },
    }),
    [isDark, rows, xLabels]
  );

  if (!rows.length || !categories.length) {
    return <div className="h-[320px] rounded-xl border border-dashed border-slate-200 bg-slate-50" />;
  }

  return <ReactApexChart options={options} series={series} type="bar" height={360} />;
}

function MetricCard({ label, value, subtitle, icon: Icon, gradientClass = "" }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm ${
        gradientClass || ""
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
        {Icon ? (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white/70 dark:bg-slate-800/70 text-indigo-600 dark:text-indigo-300 border border-indigo-100 dark:border-slate-700">
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
      </div>
      <p className="mt-1 text-3xl font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      {subtitle ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{subtitle}</p> : null}
    </div>
  );
}

export default function LangSmithDashboard() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("all");
  const [rangePreset, setRangePreset] = useState("last_1_day");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rangeLabel, setRangeLabel] = useState("Last 1 day");
  // Synchronous single-flight guard: React state updates are async, so multiple
  // effects firing in the same tick could otherwise launch concurrent requests.
  const inFlightRef = useRef(false);
  const [customRangeArmed, setCustomRangeArmed] = useState(false);
  const [startDate, setStartDate] = useState(() => toDateInputValue(new Date(Date.now() - 7 * 86400000)));
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );
  const [requestInFlight, setRequestInFlight] = useState(false);
  const [pendingFetch, setPendingFetch] = useState(false);
  const leProjects = useMemo(
    () => projects.filter((project) => String(project.id || "").startsWith("le:")),
    [projects]
  );
  const mainProjects = useMemo(
    () => projects.filter((project) => !String(project.id || "").startsWith("le:")),
    [projects]
  );

  // Totals are derived from the rows so they update live as projects stream in.
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          acc.totalTokens += Number(row.totalTokens || 0);
          acc.totalCost += Number(row.totalCost || 0);
          return acc;
        },
        { totalTokens: 0, totalCost: 0 }
      ),
    [rows]
  );

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(root.classList.contains("dark"));
    });
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const [mainSnap, leSnap] = await Promise.all([
          getDocs(collection(db, "workspaces/ai-apps-dashboard/projects")),
          getDocs(collection(db, "workspaces/ai-apps-dashboard/le-small-projects")),
        ]);

        const mainProjects = mainSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const leProjects = leSnap.docs.map((doc) => ({
          id: `le:${doc.id}`,
          ...doc.data(),
          title: doc.data().title || "Untitled LE Project",
        }));
        const data = [...mainProjects, ...leProjects];
        data.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        setProjects(data);
      } catch (err) {
        console.error("Error loading projects:", err);
        setError("Failed to load projects.");
      }
    };
    loadProjects();
  }, []);

  // Fetch usage one project at a time. Each project's result is rendered as soon
  // as it arrives and cached in localStorage, so the table fills progressively
  // and reloads are instant. One-at-a-time also keeps us well under the rate limit.
  const fetchUsage = async ({ force = false } = {}) => {
    if (inFlightRef.current) {
      setPendingFetch(true);
      return;
    }
    inFlightRef.current = true;
    setRequestInFlight(true);
    setRefreshing(true);
    setError("");

    // Only the Refresh button forces a live, cache-bypassing pull.
    const forceFresh = force;

    const preset = RANGE_PRESETS.find((p) => p.id === rangePreset);
    const rangeKey = usageRangeKey(preset, startDate, endDate);

    const rangeParams = {};
    if (preset?.id === "custom") {
      rangeParams.startMs = new Date(`${startDate}T00:00:00.000Z`).getTime();
      rangeParams.endMs = new Date(`${endDate}T23:59:59.999Z`).getTime();
    } else {
      rangeParams.days = preset?.days ?? "all";
    }

    let targetProjects;
    if (selectedProjectId === LE_PROJECTS_GROUP_ID) {
      targetProjects = leProjects;
    } else if (selectedProjectId === "all") {
      targetProjects = projects;
    } else {
      targetProjects = projects.filter(
        (p) => String(p.id) === String(selectedProjectId)
      );
    }

    if (preset?.id === "all_time") setRangeLabel("All time");
    else if (preset?.id === "custom") setRangeLabel(`${startDate} - ${endDate}`);
    else setRangeLabel(preset?.label || "");

    // 1) Seed from cache immediately so something shows right away.
    const cache = loadUsageCache();
    const rowMap = new Map();
    for (const project of targetProjects) {
      const id = String(project.id);
      const cached = cache[`${id}::${rangeKey}`];
      const hasKey = Boolean(String(project.langsmithApiKey || "").trim());
      if (cached?.row) {
        rowMap.set(id, { ...cached.row, cached: true });
      } else {
        rowMap.set(id, {
          projectId: id,
          projectName: project.title || "Unknown",
          queriedLangSmithName: null,
          totalTokens: 0,
          totalCost: 0,
          ok: false,
          status: hasKey ? "PENDING_API_SYNC" : "LANGSMITH_NOT_CONFIGURED",
          source: null,
        });
      }
    }
    setRows([...rowMap.values()]);

    // Only fetch projects that have a key AND whose cache is stale/missing
    // (unless the user forced a refresh). Fresh-cached projects cost 0 calls.
    const now = Date.now();
    const toFetch = targetProjects.filter((p) => {
      if (!String(p.langsmithApiKey || "").trim()) return false;
      if (forceFresh) return true;
      const cached = cache[`${String(p.id)}::${rangeKey}`];
      const isFresh =
        cached?.ts && now - cached.ts < USAGE_FRESH_TTL_MS && cached?.row?.ok;
      return !isFresh;
    });
    setProgress({ done: 0, total: toFetch.length });

    // 2) Fetch each stale project sequentially, updating the row as it arrives.
    try {
      let done = 0;
      for (const project of toFetch) {
        const id = String(project.id);
        try {
          const res = await fetchLangsmithProjectUsage({
            projectId: id,
            ...rangeParams,
            refresh: forceFresh,
          });
          const row = {
            projectId: id,
            projectName: project.title || res.projectName || "Unknown",
            queriedLangSmithName: res.queriedLangSmithName ?? null,
            totalTokens: res.totalTokens || 0,
            totalCost: res.totalCost || 0,
            ok: res.ok === true,
            status: res.ok ? "ok" : res.error || "error",
            source: res.source || null,
          };
          rowMap.set(id, row);
          cache[`${id}::${rangeKey}`] = { row, ts: Date.now() };
        } catch (e) {
          const cur = rowMap.get(id);
          if (cur && !cur.cached) {
            rowMap.set(id, { ...cur, status: "error" });
          }
        }
        done += 1;
        setProgress((p) => ({ ...p, done }));
        setRows([...rowMap.values()]);
      }
      saveUsageCache(cache);
    } catch (err) {
      console.error("Error loading LangSmith usage:", err);
      setError("Failed to fetch LangSmith usage.");
    } finally {
      inFlightRef.current = false;
      setRefreshing(false);
      setLoading(false);
      setRequestInFlight(false);
    }
  };

  // If filters changed while a request was already in flight,
  // run one more fetch with the latest selected values.
  useEffect(() => {
    if (requestInFlight || !pendingFetch) return;
    setPendingFetch(false);
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestInFlight, pendingFetch]);

  useEffect(() => {
    if (!projects.length) return;
    if (rangePreset === "custom") return;
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length, rangePreset, selectedProjectId]);

  useEffect(() => {
    if (!projects.length) return;
    if (rangePreset !== "custom") return;
    if (!customRangeArmed) return;
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length, rangePreset, selectedProjectId, startDate, endDate, customRangeArmed]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if ((b.totalCost || 0) !== (a.totalCost || 0)) {
          return (b.totalCost || 0) - (a.totalCost || 0);
        }
        if ((b.totalTokens || 0) !== (a.totalTokens || 0)) {
          return (b.totalTokens || 0) - (a.totalTokens || 0);
        }
        if (a.ok !== b.ok) {
          return a.ok ? -1 : 1;
        }
        return String(a.projectName || "").localeCompare(String(b.projectName || ""));
      }),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    if (!text) return sortedRows;
    return sortedRows.filter(
      (row) =>
        String(row.projectName || "").toLowerCase().includes(text) ||
        String(row.queriedLangSmithName || "").toLowerCase().includes(text)
    );
  }, [searchText, sortedRows]);

  const healthyProjects = rows.filter((r) => r.ok).length;
  const isFetching = loading || refreshing;
  // Determinate progress for the top bar; start at 8% so the bar is visible
  // immediately, and never hit 100% until the last project actually lands.
  const loadingPercent =
    progress.total > 0
      ? Math.min(98, Math.max(8, Math.round((progress.done / progress.total) * 100)))
      : 12;
  const chartWindow = useMemo(
    () => getRangeWindow(rangePreset, startDate, endDate),
    [rangePreset, startDate, endDate]
  );
  const series = useMemo(
    () => buildSyntheticSeries(totals.totalTokens, totals.totalCost, chartWindow),
    [totals.totalCost, totals.totalTokens, chartWindow]
  );
  const chartCategories = useMemo(() => series.map((point) => point.x), [series]);
  const chartRows = useMemo(
    () =>
      filteredRows.filter(
        (row) => Number(row.totalTokens || 0) > 0 || Number(row.totalCost || 0) > 0
      ),
    [filteredRows]
  );
  const chartTotalCost = Math.max(
    1,
    chartRows.reduce((acc, row) => acc + Number(row.totalCost || 0), 0)
  );

  return (
    <div className="relative min-h-screen space-y-5 bg-slate-50 dark:bg-slate-950 p-4 md:p-6">
      <div className="relative overflow-hidden sticky top-0 z-30 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm">
        {isFetching ? (
          <div className="absolute inset-x-0 top-0 z-[40] h-[3px] overflow-hidden bg-indigo-500/10">
            <div
              className="relative h-full rounded-r-full bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-400 shadow-[0_0_12px_rgba(99,102,241,0.7)] transition-[width] duration-500 ease-out"
              style={{ width: `${loadingPercent}%` }}
            >
              <span className="absolute inset-0 animate-pulse bg-white/25" />
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-500">Time Range</label>
            <select
              value={rangePreset}
              onChange={(e) => {
                const next = e.target.value;
                setRangePreset(next);
                if (next === "custom") {
                  setCustomRangeArmed(false);
                  setRangeLabel("Select custom start and end date");
                } else {
                  setCustomRangeArmed(false);
                }
              }}
              disabled={isFetching}
              className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-100"
            >
              {RANGE_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {rangePreset === "custom" ? (
            <>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-500">Start</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCustomRangeArmed(true);
                  }}
                  disabled={isFetching}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-100"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-500">End</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCustomRangeArmed(true);
                  }}
                  disabled={isFetching}
                  className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-100"
                />
              </div>
            </>
          ) : null}

          <div className={rangePreset === "custom" ? "md:col-span-2" : "md:col-span-3"}>
            <label className="text-xs font-medium text-slate-500">Project</label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={isFetching}
              className="mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-100"
            >
              <option value="all">All Projects</option>
              {mainProjects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
              {leProjects.length > 0 && (
                <option value={LE_PROJECTS_GROUP_ID}>LE Projects</option>
              )}
            </select>
          </div>

          <div className={rangePreset === "custom" ? "md:col-span-4" : "md:col-span-5"}>
            <label className="text-xs font-medium text-slate-500">Search</label>
            <div className="mt-1 flex items-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={isFetching}
                placeholder="Search projects..."
                className="w-full border-0 bg-transparent px-2 py-2 text-sm text-slate-700 dark:text-slate-100 outline-none"
              />
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <p className="text-xs text-slate-500">Range: {rangeLabel}</p>
            {isFetching ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-1 text-[11px] font-medium text-indigo-600 dark:text-indigo-300">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-500" />
                {progress.total > 0
                  ? `Loading ${progress.done}/${progress.total} projects · ${loadingPercent}%`
                  : "Loading…"}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchUsage({ force: true })}
              disabled={isFetching}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700"
            >
              <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard
          label="Total Tokens"
          value={formatCompact(totals.totalTokens)}
          subtitle={`${formatNumber(totals.totalTokens)} total`}
          icon={Coins}
          gradientClass="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-900 dark:to-indigo-950/40"
        />
        <MetricCard
          label="Total Cost"
          value={formatCurrency(totals.totalCost)}
          subtitle="USD spend"
          icon={DollarSign}
          gradientClass="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-slate-900 dark:to-emerald-950/30"
        />
        <MetricCard
          label="Projects (OK)"
          value={formatNumber(healthyProjects)}
          subtitle="healthy projects"
          icon={FolderCheck}
          gradientClass="bg-gradient-to-r from-violet-50 to-fuchsia-50 dark:from-slate-900 dark:to-violet-950/30"
        />
      </div>

      <div className={`grid grid-cols-1 gap-3 xl:grid-cols-12 ${isFetching ? "opacity-60" : ""}`}>
        <div className="xl:col-span-7">
          <ApexAreaChart points={series} bucket={chartWindow.bucket} window={chartWindow} isDark={isDark} />
        </div>

        <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 xl:col-span-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Projects by Cost</h3>
          </div>
          <div className="flex items-center justify-center">
            <ApexDonutChart rows={chartRows} totalCost={chartTotalCost} isDark={isDark} />
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 ${isFetching ? "opacity-60" : ""}`}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Cost by Project</h3>
        </div>
        <ApexCostBarChart rows={chartRows} categories={chartCategories} bucket={chartWindow.bucket} isDark={isDark} />
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">Project Usage Summary</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing {filteredRows.length} of {rows.length} project{rows.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="max-h-[420px] overflow-auto rounded-xl border border-slate-100 dark:border-slate-700">
          <table className="w-full min-w-[980px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <th className="sticky left-0 top-0 z-20 bg-white dark:bg-slate-900 py-2 pr-3 pl-3">Project</th>
                <th className="sticky top-0 z-10 bg-white dark:bg-slate-900 py-2 pr-3">Tokens</th>
                <th className="sticky top-0 z-10 bg-white dark:bg-slate-900 py-2 pr-3">Cost</th>
                <th className="sticky top-0 z-10 bg-white dark:bg-slate-900 py-2 pr-3">Avg Cost / 1K Tokens</th>
                <th className="sticky top-0 z-10 bg-white dark:bg-slate-900 py-2 pr-3">Status</th>
                <th className="sticky top-0 z-10 bg-white dark:bg-slate-900 py-2 pr-3">Trend</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const avgPer1k = Number(row.totalTokens || 0) > 0 ? (Number(row.totalCost || 0) / Number(row.totalTokens || 0)) * 1000 : 0;
                const displayProjectName = String(row.projectId || "").startsWith("le:")
                  ? "LE Projects"
                  : row.projectName;
                return (
                  <tr key={row.projectId} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 py-3 pr-3 pl-3 font-medium text-slate-800 dark:text-slate-200">
                      {displayProjectName}
                    </td>
                    <td className="py-3 pr-3">
                      <div className="font-semibold text-slate-800 dark:text-slate-200">{formatNumber(row.totalTokens)}</div>
                      <div className="mt-1 h-1.5 w-28 rounded bg-slate-100 dark:bg-slate-700">
                        <div
                          className="h-1.5 rounded bg-indigo-500"
                          style={{ width: `${Math.max(6, (Number(row.totalTokens || 0) / Math.max(1, ...filteredRows.map((r) => Number(r.totalTokens || 0)))) * 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className="py-3 pr-3 font-semibold text-slate-800 dark:text-slate-200">{formatCurrency(row.totalCost)}</td>
                    <td className="py-3 pr-3 text-slate-700 dark:text-slate-300">${avgPer1k.toFixed(4)}</td>
                    <td className="py-3 pr-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          row.ok
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        }`}
                      >
                        {row.ok ? "LANGSMITH_CONFIGURED" : row.status || "error"}
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      <svg viewBox="0 0 104 24" className="h-6 w-28">
                        <polyline
                          points={sparkPoints(Number(row.totalTokens || 0))}
                          fill="none"
                          stroke="#6366f1"
                          strokeWidth="2"
                        />
                      </svg>
                    </td>
                  </tr>
                );
              })}
              {!filteredRows.length && !loading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-slate-500 dark:text-slate-400">
                    No rows to display.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
